// Working through a set (Aug 2026): where you are in a playlist, and the way
// to the next tune, while you are looking at the chart.
//
// The controller stays mounted in the workspace header; its panel may be
// shown by a standalone popover or inside the shared Tune menu. This keeps
// auto-advance, keyboard shortcuts and lock-screen navigation active while
// the menu is closed.
//
// This is what makes a playlist a SETLIST rather than a folder — the point of
// arranging tunes in an order is playing them in that order, and before this
// the only way to reach tune 4 was to go back to the list and find it.
//
// It rides the same `playlistRows` loaders as the Playlists page, so a tune
// opened from here resolves exactly as it does there, and the cursor advances
// through the shared load seam rather than being moved by hand here.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, GripVertical, ListMusic, Plus, X } from 'lucide-react';
import { Popover } from '@/components/Popover';
import { APP_ROUTES } from '@/navigation';
import { playlistCallRow, useStore } from '@/state/store';
import { usePlaylists } from '@/data/playlistAccess';
import { playlistRows, type TuneSourceEntry } from '@/data/tuneSources';
import { getChordPlayer } from '@/audio/player';
import { describeSkipped, loadFirstPlayable } from '@/data/setPlayback';
import {
  playlistRefKey,
  resolveSetCursor,
  type PlaylistDoc,
  type PlaylistEntry,
} from '@/storage/playlists';

/** Long enough to read as a segue rather than a glitch, short enough not to
 * feel like the set has stalled. */
const ADVANCE_PAUSE_MS = 900;
import { openbookLibrary, type OpenbookLibraryEntry } from '@/data/openbookLibrary';
import { realbookLibrary, type RealbookLibraryEntry } from '@/data/realbookLibrary';
import { useLibraryTunes } from '@/data/libraryTunes';
import { PlaylistCallControls } from './PlaylistCallControls';
import { PlaylistScoreUpdate } from './PlaylistScoreUpdate';
import { canEditPlaylist } from '@/storage/playlistLinks';
import { showPlaylistAdded } from './playlistAdded';

export interface SetStripControls {
  /** The playlist's name, which the header's trigger wears. */
  name: string;
  /** "2 of 8"; "left at 2 of 8" while another tune is on screen; or that
   * your place was removed. */
  position: string;
  /** "2/8" for a narrow header's trigger; nothing while another tune is on
   * screen (the icon alone says there is a playlist); "–/8" once your place
   * was removed. */
  shortPosition: string;
  title: string;
  /** The tune on screen IS the row the cursor names (Oct 2026). False after
   * a request from the floor — a tune opened from search, a link or the
   * recents — while the place in the set is kept for the way back. */
  onEntry: boolean;
  /** The set plays on hands-free. */
  auto: boolean;
  /** Step the set from the header without opening the panel (Oct 2026:
   * mid-set, the next tune is the thing you do most, and it was two taps).
   * Null at the ends. */
  stepPrev: (() => void) | null;
  stepNext: (() => void) | null;
  /** What Next does: the next row, or the top once your place was removed. */
  nextLabel: string;
  /** A tune is loading: the steps wait. */
  busy: boolean;
  renderPanel: (close: () => void) => ReactNode;
}

/** Hands-free play, said on the trigger so it needs no opening: a word where
 * there is room, a dot on the narrow trigger (CSS). */
export function SetTriggerAutoMark() {
  return (
    <span
      className="set-trigger-auto"
      data-testid="set-strip-auto"
      title="Auto: the playlist plays on hands-free"
    >
      Auto
    </span>
  );
}

/** A custom renderer keeps the set controller mounted while its controls
 * move into a shared menu; omitting it preserves the standalone popover. */
export function SetStrip({
  children,
  formCycling = false,
}: {
  children?: (controls: SetStripControls | null) => ReactNode;
  /** Whether the band is cycling the form right now. The page decides, not
   * the persisted Cycle form toggle: that setting only takes effect on the
   * comping lens, and a seamless cycle plays one ordinary pass. */
  formCycling?: boolean;
} = {}) {
  const userPlaylists = usePlaylists();
  const setCursor = useStore((s) => s.setCursor);
  const savedCharts = useStore((s) => s.savedCharts);
  const leaveSet = useStore((s) => s.leaveSet);
  /** What starting or advancing the set passed over. Lives in the store
   * because a skip on the Playlists page has to be explained HERE. */
  const skipped = useStore((s) => s.setSkipNotice);
  const noteSetSkip = useStore((s) => s.noteSetSkip);

  const [openbook, setOpenbook] = useState<OpenbookLibraryEntry[]>([]);
  const [realbook, setRealbook] = useState<RealbookLibraryEntry[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const autoAdvance = useStore((s) => s.autoAdvanceSet);
  // Whether the tune on screen IS the row the cursor names (Oct 2026, the
  // playlist review: Giant Steps opened from a link wore "Friday gig 3 of 3",
  // and Prev and Next acted on the old place). The cursor only moves through
  // the playlist's own loads, so any other load leaves it pointing at a row
  // that is no longer on screen. The check is the one the call controls
  // already make — the row the tune was OPENED from, which survives
  // transposing and editing and is cleared by any other load — or, failing
  // that, the loaded tune's own reference, so the row's tune reopened from a
  // link or a search still counts as being on the row.
  const openedRow = useStore((s) => {
    const row = playlistCallRow(s);
    return row ? `${row.list.id}\u0000${row.entry.entryId}` : null;
  });
  const loadedRefKey = useStore((s) =>
    s.currentTuneRef ? playlistRefKey(s.currentTuneRef) : null,
  );
  const loadedTitle = useStore((s) => s.score.title.trim());
  const playlistLinks = useStore((s) => s.playlistLinks);
  /** Whether sound is actually happening, so the marker can say so honestly
   * rather than implying it. */
  const [sounding, setSounding] = useState(
    () => getChordPlayer().getTransportState() === 'playing',
  );
  useEffect(() => {
    const player = getChordPlayer();
    setSounding(player.getTransportState() === 'playing');
    return player.onTransportChange((state) => setSounding(state === 'playing'));
  }, []);
  const setAutoAdvance = useStore((s) => s.setAutoAdvanceSet);
  const trainerEnabled = useStore((s) => s.trainer.enabled);
  const [dragging, setDragging] = useState<string | null>(null);
  const movePlaylistEntry = useStore((s) => s.movePlaylistEntry);

  // Both loaders memoize their fetch, so asking again here costs nothing.
  useEffect(() => {
    void openbookLibrary().then(setOpenbook);
    void realbookLibrary().then(setRealbook);
  }, []);

  const position = useMemo(
    () => resolveSetCursor(userPlaylists, setCursor),
    [userPlaylists, setCursor],
  );
  const library = useLibraryTunes(!!position?.list.entries.some((e) => e.ref.kind === 'library'));

  const rows = useMemo(
    () =>
      position
        ? playlistRows(position.list, {
            presets: [],
            savedCharts,
            openbook,
            realbook,
            library,
            userPlaylists: [],
            playlist: null,
          })
        : [],
    [position, savedCharts, openbook, realbook, library],
  );

  // Loading through the row's own loader advances the cursor for us — the load
  // seam records it — so this never sets the cursor itself and the two can
  // never disagree.
  //
  // Note this does NOT skip past a tune that fails, though auto-advance does.
  // Pressing Next is a request for THAT tune; being carried two tunes on
  // because the first would not load takes you somewhere you did not ask to
  // go, and you are right there to decide. Auto-advance has no one watching,
  // so stopping the set is the worse failure there.
  const go = useCallback(
    async (target: PlaylistEntry) => {
      const row = rows.find((r) => r.playlistEntryId === target.entryId);
      if (!row) return;
      setFailed(null);
      noteSetSkip(null);
      setBusy(true);
      try {
        await row.load();
      } catch (error) {
        setFailed(error instanceof Error ? error.message : 'Could not load that tune.');
      } finally {
        setBusy(false);
      }
    },
    [rows, noteSetSkip],
  );

  /**
   * `[` and `]` step through the set — the convention borrowed from players and
   * DAWs, and the two keys nothing else in the app binds.
   *
   * Bound here rather than globally, so the shortcut exists exactly while the
   * strip does: a key that works on a page with no set would have nothing to
   * report and nowhere to go. Bare presses only — `⌘[`/`⌘]` stay browser back
   * and forward, which a musician mid-set is far more likely to want than a
   * shortcut we invented.
   */
  useEffect(() => {
    if (!position) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (event.key !== '[' && event.key !== ']') return;
      // Never while typing — a note, a rename, a chord, the palette. The
      // `instanceof` is load-bearing, not defensive noise: a keydown's target
      // is only an Element when something focusable has focus, and on window
      // it can be the Window itself, which has no `closest`.
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('input, textarea, select, [contenteditable="true"]')
      ) {
        return;
      }
      const step = event.key === ']' ? position.next : position.prev;
      if (!step) return;
      event.preventDefault();
      void go(step);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [position, go]);

  /**
   * Run the set hands-free.
   *
   * Fires on the sequence COMPLETING, not on the transport going idle — Stop
   * lands on idle too, and being carried to the next tune because you stopped
   * this one would be the opposite of what the button means.
   *
   * The subscription is deliberately mounted ONCE and reads what it needs
   * through a ref. Written the obvious way — with `go` and `position` as
   * dependencies — every re-render during the pause between tunes tore the
   * effect down and cancelled the pending advance, and the corpus manifests
   * resolving is exactly such a re-render. The advance simply never happened.
   *
   * The speed trainer and form cycling are excluded because they are "stay on
   * this" modes: advancing out of a trainer run would end the practice it
   * exists to do. Cycling counts only while the band is actually doing it
   * (`formCycling`): a Cycle form toggle left on where it does not apply
   * plays one ordinary pass, and holding the set there just stalls it.
   *
   * A tune that will not load is SKIPPED rather than fatal. Entries are
   * references and references go stale — a corpus tune renamed, a saved chart
   * deleted — and hands-free means nobody is there to restart the set. One
   * dead reference should cost that tune, not the rest of the run.
   */
  const advanceRef = useRef({
    enabled: false,
    next: null as PlaylistEntry | null,
    list: null as PlaylistDoc | null,
    rows: [] as TuneSourceEntry[],
  });
  advanceRef.current = {
    enabled: autoAdvance && !trainerEnabled && !formCycling,
    next: position?.next ?? null,
    list: position?.list ?? null,
    rows,
  };

  useEffect(() => {
    let timer: number | null = null;
    const off = getChordPlayer().onSequenceComplete(() => {
      const { enabled, next } = advanceRef.current;
      if (!enabled || !next) return;
      // A beat of air: the next tune has to load, and landing straight on a
      // downbeat from the last one read as a glitch rather than a segue.
      timer = window.setTimeout(() => {
        // Read the ref AGAIN here rather than closing over the values above:
        // the pause is long enough for the set to have been edited under us.
        const { list, next: target, rows: currentRows } = advanceRef.current;
        if (!list || !target) return;
        setFailed(null);
        setBusy(true);
        void loadFirstPlayable(list, target, currentRows)
          .then(({ played, skipped: passed, error }) => {
            useStore.getState().noteSetSkip(describeSkipped(passed));
            // Only ask for playback if something actually loaded. Playing
            // regardless would restart the tune that just finished, which
            // reads as the set being stuck rather than as an error.
            if (played) useStore.getState().requestPlayback();
            else setFailed(error?.message ?? 'Nothing further in this playlist would load.');
          })
          .finally(() => setBusy(false));
      }, ADVANCE_PAUSE_MS);
    });
    return () => {
      off();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, []);

  /**
   * Lock-screen Next and Previous (MediaSession) step the set exactly as `[`
   * and `]` do — but hands-free, so a dead row is skipped the way auto-advance
   * skips it: the screen is locked, and nobody is there to read a failure.
   * The tune it lands on then PLAYS: on a media control "next" means "play
   * the next one", and a loaded-but-idle tune would drop the very Now Playing
   * card the button lives on. Each side is offered only while the set has a
   * tune that way, so the OS hides Next at the end of the set rather than
   * showing a dead button. (Previous walks forward from the previous row, so a
   * dead previous lands back on the current tune from the top — a restart,
   * which is the honest reading of "previous" when the previous is gone.)
   */
  const lockRef = useRef({
    list: null as PlaylistDoc | null,
    next: null as PlaylistEntry | null,
    prev: null as PlaylistEntry | null,
    rows: [] as TuneSourceEntry[],
  });
  lockRef.current = {
    list: position?.list ?? null,
    next: position?.next ?? null,
    prev: position?.prev ?? null,
    rows,
  };
  const inSet = position !== null;
  const hasNext = !!position?.next;
  const hasPrev = !!position?.prev;
  useEffect(() => {
    const player = getChordPlayer();
    if (!inSet) {
      player.setMediaNavigation(null);
      return;
    }
    const step = (side: 'next' | 'prev') => {
      const { list, rows: currentRows } = lockRef.current;
      const target = lockRef.current[side];
      if (!list || !target) return;
      setFailed(null);
      setBusy(true);
      void loadFirstPlayable(list, target, currentRows)
        .then(({ played, skipped: passed, error }) => {
          useStore.getState().noteSetSkip(describeSkipped(passed));
          if (played) useStore.getState().requestPlayback();
          else setFailed(error?.message ?? 'Nothing further in this playlist would load.');
        })
        .finally(() => setBusy(false));
    };
    player.setMediaNavigation({
      ...(hasNext ? { next: () => step('next') } : {}),
      ...(hasPrev ? { previous: () => step('prev') } : {}),
    });
    return () => player.setMediaNavigation(null);
  }, [inSet, hasNext, hasPrev]);

  if (!position) return children?.(null) ?? null;
  const { list, entry, index, total, prev, next } = position;
  const onEntry =
    !!entry &&
    (openedRow === `${list.id}\u0000${entry.entryId}` ||
      (loadedRefKey !== null && playlistRefKey(entry.ref) === loadedRefKey));
  // Another tune is on screen. The place is KEPT — mid-gig you take a request
  // from the floor and carry on after it, and the set already keeps your
  // place when a row is removed from under you — the strip just stops
  // claiming you are on it, and offers the way back.
  const away = !!entry && !onEntry;
  const where = entry ? `${index + 1} of ${total}` : null;
  const positionText = where
    ? away
      ? `left at ${where}`
      : where
    : 'not in this playlist any more';
  // Whether the tune on screen could be added here: it has a name, it is not
  // the row you are on or already in the list, and the list is yours to edit.
  const canAddLoaded =
    !!loadedTitle &&
    loadedTitle !== 'Untitled Chart' &&
    !onEntry &&
    !(loadedRefKey && list.entries.some((e) => playlistRefKey(e.ref) === loadedRefKey)) &&
    canEditPlaylist(playlistLinks, list.id);
  const shortPositionText = entry ? (away ? '' : `${index + 1}/${total}`) : `–/${total}`;
  const title = `${list.name} — ${
    where ? (away ? `you left at tune ${where}` : `tune ${where}`) : 'your place was removed'
  }${autoAdvance ? ', playing hands-free' : ''}`;

  const renderPanel = (close: () => void) => (
    <div className="set-panel" aria-label={`Playlist: ${list.name}`}>
      <div className="set-strip-where">
        <Link className="set-strip-name" to={`${APP_ROUTES.playlists}/${list.id}`} onClick={close}>
          {list.name}
        </Link>
        <span className="set-panel-position muted" data-testid="set-panel-position">
          {where ? (away ? `You left at ${where}` : where) : 'Not in this playlist any more'}
        </span>
        {entry?.note && !away && (
          <span className="set-strip-note" data-testid="set-strip-note">
            {entry.note}
          </span>
        )}
      </div>

      {failed && (
        <p className="set-strip-error" role="alert">
          {failed}
        </p>
      )}

      {skipped && (
        // Not an alert: the set is still running, and nothing needs doing.
        // But an unexplained jump over a tune looks like a bug, so it says so.
        <p className="set-strip-skipped" role="status" data-testid="set-strip-skipped">
          {skipped}
        </p>
      )}

      {away && entry && (
        // The way back to where you left the set, on its own row above Prev
        // and Next, which still step from that place.
        <div className="set-strip-return">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            data-testid="set-back"
            aria-label={`Back to ${entry.ref.title}, ${where}`}
            title={`Back to ${entry.ref.title}, ${where}`}
            disabled={busy}
            onClick={() => void go(entry)}
          >
            Back to {where}
          </button>
        </div>
      )}

      <div className="set-strip-actions">
        <button
          type="button"
          className={`btn btn-sm${autoAdvance ? ' is-active' : ''}`}
          data-testid="set-auto-advance"
          aria-pressed={autoAdvance}
          title={
            trainerEnabled || formCycling
              ? 'Auto-play is paused while the trainer or form cycling is on — those stay on one tune'
              : 'Play the playlist hands-free: each tune moves to the next when it ends'
          }
          onClick={() => setAutoAdvance(!autoAdvance)}
        >
          Auto
        </button>
        <button
          type="button"
          className="btn btn-sm"
          data-testid="set-prev"
          aria-label="Previous tune in the playlist"
          title="Previous tune in the playlist  ["
          disabled={!prev || busy}
          onClick={() => prev && void go(prev)}
        >
          <ChevronLeft size={16} aria-hidden="true" />
          Prev
          <kbd className="set-strip-key" aria-hidden="true">
            [
          </kbd>
        </button>
        <button
          type="button"
          className="btn btn-sm"
          data-testid="set-next"
          // The label changes with the situation: after a row is removed from
          // under the cursor, "Next" would be a lie about where you would land.
          aria-label={entry ? 'Next tune in the playlist' : 'Start this playlist from the top'}
          title={entry ? 'Next tune in the playlist  ]' : 'Start this playlist from the top  ]'}
          disabled={!next || busy}
          onClick={() => next && void go(next)}
        >
          {entry ? 'Next' : 'Start from the top'}
          <ChevronRight size={16} aria-hidden="true" />
          <kbd className="set-strip-key" aria-hidden="true">
            ]
          </kbd>
        </button>
        <button
          type="button"
          className="close-circle set-strip-leave"
          data-testid="set-leave"
          aria-label={`Leave ${list.name}`}
          onClick={() => {
            leaveSet();
            close();
          }}
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>

      <PlaylistCallControls inPlaylistPanel />

      <ol className="set-queue" data-testid="set-queue">
        {rows.map((row, i) => {
          const rowEntryId = row.playlistEntryId!;
          const isNow = rowEntryId === entry?.entryId;
          // Only a definite 'missing' marks the row. 'unknown' means the
          // corpus manifest has not landed yet, and a set that flashes
          // "unavailable" across itself on every load would be worse than
          // one that waits a beat.
          const isGone = row.resolvable === 'missing';
          return (
            <li
              key={row.key}
              className={`set-queue-row${isNow ? ' is-now' : ''}${
                isGone ? ' is-gone' : ''
              }${dragging === rowEntryId ? ' is-dragging' : ''}`}
              // The whole set, not just what is ahead: a bandleader glances
              // back at what has been played as often as forward, and a set
              // is eight tunes rather than eight hundred.
              aria-current={isNow ? 'true' : undefined}
              draggable
              onDragStart={() => setDragging(rowEntryId)}
              onDragEnd={() => setDragging(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (dragging) movePlaylistEntry(list.id, dragging, i);
                setDragging(null);
              }}
            >
              <span className="set-queue-grip" aria-hidden="true">
                <GripVertical size={13} />
              </span>
              <span className="set-queue-index muted">{i + 1}</span>
              <button
                type="button"
                className="set-queue-open"
                // The state goes in the NAME, not only the styling: a row
                // that reads "Play Blue Bossa" to a screen reader while
                // looking struck through to everyone else is a worse lie
                // than no marking at all.
                aria-label={
                  isGone
                    ? `${row.title}, ${i + 1} of ${total} — unavailable`
                    : `Play ${row.title}, ${i + 1} of ${total}`
                }
                // Deliberately NOT disabled: 'missing' is a strong signal,
                // not proof, and the player keeps the option to try.
                disabled={busy}
                onClick={() => {
                  void go(list.entries[i]);
                  close();
                }}
                onKeyDown={(e) => {
                  if (!e.altKey) return;
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    movePlaylistEntry(list.id, rowEntryId, i - 1);
                  }
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    movePlaylistEntry(list.id, rowEntryId, i + 1);
                  }
                }}
              >
                <span className="set-queue-title">{row.title}</span>
                {row.melody && (
                  <span className="tune-melody" title="Has a written melody" aria-hidden="true">
                    {'♪'}
                  </span>
                )}
                {isGone && (
                  <span className="set-queue-gone" data-testid="set-queue-gone">
                    unavailable
                  </span>
                )}
                <span className="set-queue-meta muted">
                  {/* No artwork: a chart has no cover, and key + style is what
                    a player actually needs off a glance anyway. */}
                  {[row.musicKey, row.style].filter(Boolean).join(' · ')}
                </span>
              </button>
              {row.note && <span className="set-queue-note">{row.note}</span>}
              {isNow && onEntry && (
                // "Now" claimed sound whether or not any was happening. The
                // accent row already says WHERE you are; this only speaks up
                // when it can say something the row cannot — and not while
                // another tune is the one sounding.
                <span className="set-queue-now" data-testid="set-queue-now">
                  {sounding ? 'Playing' : ''}
                </span>
              )}
              <PlaylistScoreUpdate row={row} onUpdated={() => setFailed(null)} />
            </li>
          );
        })}
      </ol>

      {canAddLoaded && (
        // The tune on screen, one tap into the playlist you are working
        // through (user, Oct 2026: Add to playlist should be "part of the
        // playlist drop down"). Added at the end; your place stays put.
        <button
          type="button"
          className="set-queue-add"
          data-testid="set-add-current"
          disabled={busy}
          onClick={() => {
            const { entryId } = useStore.getState().addCurrentTuneToPlaylist(list.id);
            if (!entryId) {
              setFailed('Could not add this tune to the playlist.');
              return;
            }
            showPlaylistAdded({ playlistId: list.id, playlistName: list.name, entryId });
          }}
        >
          <Plus size={15} aria-hidden="true" />
          <span className="set-queue-add-title">Add {loadedTitle}</span>
        </button>
      )}
    </div>
  );
  if (children) {
    return children({
      name: list.name,
      position: positionText,
      shortPosition: shortPositionText,
      title,
      onEntry,
      auto: autoAdvance,
      stepPrev: prev ? () => void go(prev) : null,
      stepNext: next ? () => void go(next) : null,
      nextLabel: entry ? 'Next tune in the playlist' : 'Start this playlist from the top',
      busy,
      renderPanel,
    });
  }

  return (
    <Popover
      testid="set-strip-trigger"
      align="right"
      title={title}
      label={
        <span className={`set-trigger${onEntry ? '' : ' set-trigger-away'}`} data-testid="set-strip">
          <ListMusic size={15} aria-hidden="true" />
          {/* The position rides on the TRIGGER: the one thing worth knowing
              without opening anything is how far through the set you are. */}
          <span className="set-trigger-position" data-testid="set-strip-position">
            {positionText}
          </span>
          {autoAdvance && <SetTriggerAutoMark />}
        </span>
      }
    >
      {renderPanel}
    </Popover>
  );
}
